import { IconButton } from "@hortis/ui/button";
import { formatDate } from "@hortis/ui/date-picker";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@hortis/ui/hover-card";
import {
  Building6,
  CalendarMinus2,
  Edit2,
  IpenNumber,
  Package,
  Tag3,
} from "@hortis/ui/icons";
import { CalendarPlus2 } from "@hortis/ui/icons/calendar-plus-2";
import { Globe2 } from "@hortis/ui/icons/globe-2";
import { Tag } from "@hortis/ui/tag";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { AccessionFieldsFragment } from "generated/graphql";
import { useState } from "react";
import TimeAgo, { Formatter } from "react-timeago";
import {
  MobileProperty,
  RecordProperties,
  RecordPropertiesGroup,
  RecordPropertiesGroupTitle,
  RecordPropertiesItem,
  RecordPropertiesItemLabel,
  RecordPropertiesItemValue,
  ViewProperty,
} from "src/components/record-properties/record-properties";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import { twMerge } from "tailwind-merge";

const timeAgoFormatter: Formatter = (
  value,
  unit,
  suffix,
  epochMilliseconds,
  nextFormatter,
) =>
  unit === "second" || unit === "minute" || unit === "hour"
    ? "Today"
    : nextFormatter == null
      ? undefined
      : nextFormatter(value, unit, suffix, epochMilliseconds);

export const AccessionViewProperties = ({
  accession,
  className,
}: {
  accession: NonNullable<AccessionFieldsFragment>;
  className?: string;
}) => {
  const tags = accession.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("flex flex-wrap gap-x-3 gap-y-1.5", className)}>
      {accession.accessionDate != null && (
        <Tooltip>
          <TooltipTrigger>
            <ViewProperty>
              <CalendarPlus2 className="h-4 w-4" />
              <TimeAgo
                date={accession.accessionDate}
                formatter={timeAgoFormatter}
                title=""
              />
            </ViewProperty>
          </TooltipTrigger>
          <TooltipContent>
            Accessioned on {formatDate(parseUtcDate(accession.accessionDate))}
          </TooltipContent>
        </Tooltip>
      )}
      {accession.deaccessionDate != null && (
        <ViewProperty>
          <CalendarMinus2 className="h-4 w-4" />
          {formatDate(parseUtcDate(accession.deaccessionDate))}
        </ViewProperty>
      )}
      {accession.provenanceCategory != null && (
        <ViewProperty>
          <Globe2 className="h-4 w-4" />
          {accession.provenanceCategory.name}
        </ViewProperty>
      )}
      {accession.materialType != null && (
        <ViewProperty>
          <Package className="h-4 w-4" />
          {accession.materialType.name}
        </ViewProperty>
      )}
      {accession.donor != null && (
        <ViewProperty>
          <Building6 className="h-4 w-4" />
          {accession.donor}
        </ViewProperty>
      )}
      {accession.ipenNumber != null && (
        <ViewProperty>
          <IpenNumber className="h-4 w-4" />
          {accession.ipenNumber}
        </ViewProperty>
      )}
      {tags.length > 0 && (
        <HoverCard>
          <HoverCardTrigger className="cursor-pointer">
            <ViewProperty>
              <Tag3 className="h-4 w-4" />
              {tags.length > 2
                ? `${tags.length} tags`
                : tags.map(({ name }) => name).join(", ")}
            </ViewProperty>
          </HoverCardTrigger>
          <HoverCardContent className="p-3 space-y-1.5 max-w-xs" align="start">
            <div className="font-medium text-sm text-grey-500">Tags</div>
            <div className="flex flex-wrap items-center gap-1">
              {tags.map(({ id, name }) => (
                <Tag
                  key={id}
                  data-cy="taxon-tag"
                  size="lg"
                  className="px-1.5 py-0.5"
                >
                  {name}
                </Tag>
              ))}
            </div>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  );
};

export const AccessionMobileProperties = ({
  accession,
  onRequestEdit,
  canEdit,
  className,
}: {
  accession: NonNullable<AccessionFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
}) => {
  const tags = accession.tagsConnection?.nodes ?? [];
  return (
    <div className={twMerge("space-y-1 pb-1", className)}>
      <div className="flex flex-wrap gap-1">
        {accession.accessionDate != null && (
          <Tooltip>
            <TooltipTrigger>
              <MobileProperty data-cy="accession-date-picker">
                <CalendarPlus2 className="h-4 w-4" />
                <TimeAgo
                  date={accession.accessionDate}
                  formatter={timeAgoFormatter}
                  title=""
                />
              </MobileProperty>
            </TooltipTrigger>
            <TooltipContent>
              Accessioned on {formatDate(parseUtcDate(accession.accessionDate))}
            </TooltipContent>
          </Tooltip>
        )}
        {accession.deaccessionDate != null && (
          <MobileProperty data-cy="deaccession-date-picker">
            <CalendarMinus2 className="h-4 w-4" />
            {formatDate(parseUtcDate(accession.deaccessionDate))}
          </MobileProperty>
        )}
        {accession.provenanceCategory != null && (
          <MobileProperty data-cy="provenance-picker">
            <Globe2 className="h-4 w-4" />
            {accession.provenanceCategory.name}
          </MobileProperty>
        )}
        {accession.materialType != null && (
          <MobileProperty data-cy="material-received-picker">
            <Package className="h-4 w-4" />
            {accession.materialType.name}
          </MobileProperty>
        )}
        {accession.donor != null && (
          <MobileProperty data-cy="supplier-picker">
            <Building6 className="h-4 w-4" />
            {accession.donor}
          </MobileProperty>
        )}
        {accession.ipenNumber != null && (
          <MobileProperty data-cy="ipen-number-picker">
            <IpenNumber className="h-4 w-4" />
            {accession.ipenNumber}
          </MobileProperty>
        )}
        {tags.length > 0 && (
          <HoverCard>
            <HoverCardTrigger className="cursor-pointer">
              <MobileProperty data-cy="tags-picker">
                <Tag3 className="h-4 w-4" />
                {tags.length > 2
                  ? `${tags.length} tags`
                  : tags.map(({ name }) => name).join(", ")}
              </MobileProperty>
            </HoverCardTrigger>
            <HoverCardContent
              className="p-3 space-y-1.5 max-w-xs"
              align="start"
            >
              <div className="font-medium text-sm text-grey-500">Tags</div>
              <div className="flex flex-wrap items-center gap-1">
                {tags.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </div>
            </HoverCardContent>
          </HoverCard>
        )}
        <div className="flex items-center gap-1">
          {canEdit && !accession.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="edit-properties-button"
                  size="xs"
                  variant="tertiaryGray"
                  className="bg-grey-50 rounded-full p-1.5 hover:bg-grey-100 border border-grey-100"
                  icon={Edit2}
                />
              </TooltipTrigger>
              <TooltipContent side="bottom">Edit properties</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export const AccessionProperties = ({
  accession,
  canEdit,
  onRequestEdit,
  className,
}: {
  accession: NonNullable<AccessionFieldsFragment>;
  onRequestEdit: () => void;
  canEdit: boolean;
  className?: string;
}) => {
  const [taxonExpanded, setTaxonExpanded] = useState(false);
  return (
    <RecordProperties className={twMerge("", className)}>
      <RecordPropertiesGroup>
        <RecordPropertiesGroupTitle>
          Properties
          {canEdit && !accession.archived && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton
                  onClick={onRequestEdit}
                  ariaLabel="Edit section"
                  data-cy="edit-properties-button"
                  size="sm"
                  variant="tertiaryGray"
                  icon={Edit2}
                  className="hover:bg-grey-100 absolute -right-2 p-1.5"
                />
              </TooltipTrigger>
              <TooltipContent side="left">Edit accession</TooltipContent>
            </Tooltip>
          )}
        </RecordPropertiesGroupTitle>
        {accession.accessionDate != null && (
          <RecordPropertiesItem data-cy="accession-date-picker">
            <RecordPropertiesItemLabel>
              Accession date
            </RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {formatDate(parseUtcDate(accession.accessionDate))}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.deaccessionDate != null && (
          <RecordPropertiesItem data-cy="deaccession-date-picker">
            <RecordPropertiesItemLabel>
              Deaccession date
            </RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {formatDate(parseUtcDate(accession.deaccessionDate))}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.provenanceCategory != null && (
          <RecordPropertiesItem data-cy="provenance-picker">
            <RecordPropertiesItemLabel>Provenance</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {accession.provenanceCategory.name}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.materialType != null && (
          <RecordPropertiesItem data-cy="material-received-picker">
            <RecordPropertiesItemLabel>
              Material received
            </RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {accession.materialType.name}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.donor != null && (
          <RecordPropertiesItem data-cy="supplier-picker">
            <RecordPropertiesItemLabel>Supplier</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {accession.donor}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.ipenNumber != null && (
          <RecordPropertiesItem data-cy="ipen-number-picker">
            <RecordPropertiesItemLabel>IPEN number</RecordPropertiesItemLabel>
            <RecordPropertiesItemValue>
              {accession.ipenNumber}
            </RecordPropertiesItemValue>
          </RecordPropertiesItem>
        )}
        {accession.tagsConnection?.nodes != null &&
          accession.tagsConnection.nodes.length > 0 && (
            <RecordPropertiesItem className="gap-2" data-cy="tags-picker">
              <RecordPropertiesItemLabel>Tags</RecordPropertiesItemLabel>
              <RecordPropertiesItemValue className="flex flex-wrap gap-1.5">
                {accession.tagsConnection.nodes.map(({ id, name }) => (
                  <Tag
                    key={id}
                    data-cy="taxon-tag"
                    size="lg"
                    className="px-1.5 py-0.5"
                  >
                    {name}
                  </Tag>
                ))}
              </RecordPropertiesItemValue>
            </RecordPropertiesItem>
          )}
      </RecordPropertiesGroup>
    </RecordProperties>
  );
};
